import { inventoryFloorRefusal } from '../compile/inventory-facet';
import { encounterCreatureField, posCoord } from '../../shared/array-types.js';
// Pure FT3D push/pull planning: inventory absorb/shove, creature hard stops, destination placement, grapple drag and support falls.
// The ability caller writes positions and routes collision/fall damage through the shared browser lanes. Unknown geometry,
// transport shoves, zones and unserved fall contracts retain named refusals. Defenses remain the caller's gate.
import { predictFall, type FallReaders, type FallVerdict } from './fall-check.js';
import { floorBandShape } from '../inventory/lift-occupancy.js';
import { fallingShapeSupportFloor, type RestShape } from '../inventory/floor-move.js';
import { floorAt } from '../../../../ft3d/engine/ft3d-shape-core.js';
import { wallStop } from './wall-stop.js';
import { statusItemIdOf } from './squeeze-at-rest.js';
import { resolveMoveDestination, type MoveDestination } from './move-destination.js';
import { compoundActorIds, planLatchedObjectMove } from './compound-drag.js';
import { distanceToZoneFt, toLocal, type Zone } from '../inventory/floor-move.js';
import { readInventoryField } from '../compile/inventory-raw-readers.js';
import { inventoryHasPosition } from './inventory-support.js';
import { squeezeBodyFt } from '../../../../ft3d/engine/ft3d-creature-size.js';
import { moverMovementInt } from './difficult-terrain.js';

export const GRAPPLED_ITEM_ID = '0C2A8037-B0BC-8300-C818-198881B00E7D';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const rows = (value: unknown): Row[] => Array.isArray(value) ? value.filter(v => record(v) !== null) as Row[] : [];
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();
const EPS = 1e-6;

export interface ForcedMoveReaders extends FallReaders {
  /** Explicit rigid/full-radius controls; ordinary creature displacement uses the squeeze ladder. */
  allowSqueeze?: boolean;
  /** The current encounter's scene projection; absent means the authored shape's overlays remain unknown. */
  runtimeShape?: RestShape | null;
  /** Shared collision-envelope width in ft; a standee uses its size tier. Null = footprint not held. */
  baseSizeFt: (row: Row) => number | null;
  /** ft_ground_occupancy_height_ft(): the collision height (mesh body, else the size-tier cube). */
  heightFt: (row: Row) => number | null;
  /** A physical mesh closes navigation apertures shorter than its banked body. */
  meshBody?: (row: Row) => boolean;
  /** janus_creature_has_forced_movement_defense(row, 'immune_to_forced_movement'); null = a source not held. */
  immuneToForcedMovement: (row: Row) => boolean | null;
  /** Ordinary walking's friend/enemy membership for hidden-body filtering. */
  opposingTeams?: (left: Row, right: Row) => boolean;
  /** Would this inventory row be skipped by the sweep (no-cover template, bound curtain, transition prop)? null = unknown. */
  inventoryPassThrough: (row: Row) => boolean | null;
  /** The farthest reach of an inventory row's footprint from its centre, ft (null = not provable: a transformed mesh, a wall run). */
  inventoryExtentFt: (row: Row) => number | null;
  /** Physical sweep/landing footprint and height; size-tier absorption capacity is independent of geometry. */
  inventoryCollision?: (row: Row, mover?: Row) => { diameter: number; height: number; capacity: number; obstacle: Zone;
    solid: boolean; steppedOver?: boolean; loot?: boolean; shoveRefusal?: string } | null;
}
export type Point = { x: number; y: number; z: number };

/** Sweep membership shared by forced displacement and pre-attack approaches. Geometry-only
 * readers retain their conservative extent check; known loot/step-over/passage rows never block. */
export function inventoryBlocksSweep(inv: Row, read: ForcedMoveReaders, mover: Row): boolean | null {
  if (Number(readInventoryField(inv, 'inventory_deleted') ?? 0) === 1 || !inventoryHasPosition(inv, read.position)) return false;
  const pass = read.inventoryPassThrough(inv);
  if (pass === null) return null;
  if (pass) return false;
  return read.inventoryCollision ? read.inventoryCollision(inv, mover)?.solid ?? null : true;
}

export interface ForcedMoveInput {
  encounter: Row;
  shape: RestShape | null;
  caster: Row;
  target: Row;
  /** The cast's AoE template row (the encounter-shaped row PHP reads target_x_pos / status_origin_point_* from), or null. */
  aoe?: Row | null;
  /** Current placed cast's normalized midpoint. Null = requested reference position is not held. */
  reference?: Point | null;
  push: number;
  pull: number;
  toCasterCenter: boolean;
}
export interface CollisionPlan { kind: 'creature' | 'inventory'; id: string; contactFt: number; absorbedFt: number; dice: number; struckDice: number }
export interface ForcedMovePlan {
  kind: 'move';
  start: Point; end: Point;
  collision: CollisionPlan | null;
  inventoryMove?: { id: string; start: Point; end: Point };
  /** The target's fall at the landing (none / fall). */
  fall: FallVerdict;
  /** Rows the grapple drags by the same delta, each with its own fall. */
  dragged: Array<{ id: string; start: Point; end: Point; fall: FallVerdict }>;
  /** A persistent zone anchor exists: the caller runs MV2's exit reconcile after the commit (PHP runs it after every displacement). */
  zoneReconcile: boolean;
}
export type ForcedMoveResult = { kind: 'none'; why: 'no-distance' | 'at-reference' | 'no-displacement' | 'destination-blocked' } | ForcedMovePlan | { kind: 'handback'; reason: string };
const hand = (reason: string): Extract<ForcedMoveResult, { kind: 'handback' }> => ({ kind: 'handback', reason: 'forced-move:' + reason });

const present = (value: unknown): boolean => value !== null && value !== undefined;
/** get_target_point_x / _z() (functions.php): target_*_pos[0], else the midpoint of a 4-point template's edge (both set, neither
 *  ''), else status_distance_point_* ?? pos_coord ?? 0 — ZERO, not the target's own coordinate (Codex S2 F4). */
function targetPoint(row: Row, axis: 'x' | 'z'): number {
  const direct = row[`target_${axis}_pos`];
  if (Array.isArray(direct) && present(direct[0])) return Number(direct[0]);
  const position = record(row.position);
  const o = position?.[`status_origin_point_${axis}`], d = position?.[`status_distance_point_${axis}`];
  if (present(o) && present(d) && o !== '' && d !== '') return (Number(o) + Number(d)) / 2;
  const own = row[`status_distance_point_${axis}`];
  return Number(present(own) ? own : posCoord(row, `${axis}_pos`) ?? 0);
}
/** ft_shape_aoe_center_y() (ft3d_shape_core.php): a 4-point template's depth midpoint — both exactly 0 without ft3d_depth_bearing is
 *  "never supplied" → null; else the row's own y (null unless depth-bearing, then 0). The caller falls back to the target's y. */
function aoeCenterY(row: Row): number | null {
  const position = record(row.position);
  const o = position?.status_origin_point_y, d = position?.status_distance_point_y;
  const numeric = (v: unknown): boolean => typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)));
  if (numeric(o) && numeric(d)) {
    if (Number(o) === 0 && Number(d) === 0 && !position?.ft3d_depth_bearing) return null;
    return (Number(o) + Number(d)) / 2;
  }
  const y = posCoord(row, 'y_pos');
  return y === undefined ? (position?.ft3d_depth_bearing ? 0 : null) : y;
}
const idOf = (row: Row): string => upper(record(row.encounter_creatures)?.encounter_creature_id);
const LINKS = ['riding', 'ridden', 'piloting', 'piloted'];

/** collision_swept_entry_distance(): the forward entry distance of the swept square against one footprint, or null. */
export function sweptEntryDistance(rect: { left: number; right: number; near: number; far: number }, tx: number, ty: number,
  ux: number, uy: number, hw: number, hh: number, length: number): number | null {
  const EL = rect.left - hw, ER = rect.right + hw, EB = rect.near - hh, ET = rect.far + hh;
  let txNear: number, txFar: number, tyNear: number, tyFar: number;
  if (Math.abs(ux) < EPS) { if (tx < EL || tx > ER) return null; txNear = -Infinity; txFar = Infinity; }
  else { const a = (EL - tx) / ux, b = (ER - tx) / ux; txNear = Math.min(a, b); txFar = Math.max(a, b); }
  if (Math.abs(uy) < EPS) { if (ty < EB || ty > ET) return null; tyNear = -Infinity; tyFar = Infinity; }
  else { const a = (EB - ty) / uy, b = (ET - ty) / uy; tyNear = Math.min(a, b); tyFar = Math.max(a, b); }
  const near = Math.max(txNear, tyNear), far = Math.min(txFar, tyFar);
  if (near > far || near < 0 || near >= length - EPS) return null;
  return near;
}

/** Moving circular body against the actual obstacle. In an oblong's local frame its expanded footprint
 * is two strips and four round corners, preserving rotation without an oversized world-aligned box. */
function sweptObstacleEntryDistance(zone: Zone, x: number, y: number, ux: number, uy: number, radius: number, length: number): number | null {
  // Preserve the sweep's existing escape from an overlap at the starting foot.
  if (distanceToZoneFt(zone, x, y)[0] < radius - EPS) return null;
  const circleEntry = (tx: number, ty: number, dx: number, dy: number, cx: number, cy: number, r: number): number | null => {
    const px = tx - cx, py = ty - cy, along = px * dx + py * dy;
    const discriminant = along * along - (px * px + py * py - r * r);
    if (discriminant < 0) return null;
    const entry = -along - Math.sqrt(discriminant);
    return entry >= 0 && entry < length - EPS ? entry : null;
  };
  if (zone.kind === 'circle') return circleEntry(x, y, ux, uy, zone.x, zone.y, zone.r + radius);
  const [tx, ty] = toLocal(zone, x, y), c = Math.cos(zone.facing), s = Math.sin(zone.facing);
  const dx = ux * c + uy * s, dy = -ux * s + uy * c;
  const candidates: Array<number | null> = [
    sweptEntryDistance({ left: -zone.halfW - radius, right: zone.halfW + radius, near: -zone.halfD, far: zone.halfD }, tx, ty, dx, dy, 0, 0, length),
    sweptEntryDistance({ left: -zone.halfW, right: zone.halfW, near: -zone.halfD - radius, far: zone.halfD + radius }, tx, ty, dx, dy, 0, 0, length),
  ];
  for (const cx of [-zone.halfW, zone.halfW]) for (const cy of [-zone.halfD, zone.halfD])
    candidates.push(circleEntry(tx, ty, dx, dy, cx, cy, radius));
  const entries = candidates.filter((value): value is number => value !== null);
  return entries.length ? Math.min(...entries) : null;
}

/** move_target()'s ft_resolve_move_destination(…, ft3d_walk: true, free) for one moved row (move-destination.ts, session 3 step 2): PLACED (maybe
 *  nudged), BLOCKED (PHP leaves the row where it was) or UNKNOWN (hand back). The obstacles are ft_encounter_occupancy_obstacles() for the
 *  row — EVERY other creature (no distance cutoff) except its compound (ft_compound_actor_ids) and private rows; a corpse no taller than the
 *  step is neither an obstacle nor a support. Unknown (Codex step-2 review F2/F3): a living body of unproven size anywhere; within reach of
 *  the resolver's nudge search, a taller corpse (also a support top, maybe carried), or an unheld hidden-body team relation.
 *  Held inventory contributes its destination obstacles; unheld bodies retain a named refusal. */
export function destination(input: Pick<ForcedMoveInput, 'encounter' | 'shape'>, read: ForcedMoveReaders, row: Row, start: Point, end: Point, free: boolean, wallContact = false): MoveDestination {
  const shape = read.runtimeShape ?? input.shape!;
  const base = read.baseSizeFt(row), height = read.heightFt(row);
  if (base === null || height === null || !(base > 0) || !(height > 0)) return { status: 'unknown', reason: 'body' };
  const compound = compoundActorIds(input.encounter, row, read);
  const step = Math.max(2.5, base);
  const obstacles: Zone[] = [];
  const doubtful: Array<{ x: number; y: number; r: number; reason: string }> = [];
  for (const other of rows(input.encounter.encounter_creatures_arr)) {
    const id = idOf(other);
    if (id === '' || compound.has(id)) continue;
    if (Number(scalar(record(other.encounter_creatures)?.encounter_creature_private) ?? 0) === 1) continue;
    const hidden = read.hasStatus(other, 'hidden');
    if (hidden && read.opposingTeams?.(row, other)) continue;
    const [ox, oy, oz] = read.position(other);
    if (![ox, oy, oz].every(Number.isFinite)) return { status: 'unknown', reason: 'other-position' };
    const ob = read.baseSizeFt(other), oh = read.heightFt(other);
    const dead = Number(scalar(record(other.encounter_creatures)?.encounter_creature_dead) ?? 0) === 1;
    if (dead) {
      // ft3d_corpse_support_height_ft(): a size-tier card lies as tall as its width; mesh / swarm / unsized corpses are not modelled.
      if (ob !== null && ob <= step) continue;
      if (ob === null) return { status: 'unknown', reason: 'corpse-size' };
      doubtful.push({ x: ox, y: oy, r: ob / 2, reason: 'corpse' });
      continue;
    }
    if (ob === null || oh === null) return { status: 'unknown', reason: 'other-body' };
    if (hidden && !read.opposingTeams) { doubtful.push({ x: ox, y: oy, r: ob / 2, reason: 'hidden-body' }); continue; }
    obstacles.push({ kind: 'circle', x: ox, y: oy, r: ob / 2, zMin: oz, zMax: oz + oh } as Zone);
  }
  if (read.inventoryCollision) for (const inv of rows(input.encounter.encounter_inventories_arr)) {
    if (upper(readInventoryField(inv, 'inventory_id')) === upper(readInventoryField(row, 'inventory_id')) && readInventoryField(row, 'inventory_id')) continue;
    if (Number(readInventoryField(inv, 'inventory_deleted') ?? 0) === 1 || !inventoryHasPosition(inv, read.position)) continue;
    const body = read.inventoryCollision(inv, row);
    if (!body) return { status: 'unknown', reason: 'inventory-body' };
    if (body.solid) obstacles.push(body.obstacle);
  }
  // resolvePlacement's search: each pass moves the candidate less than twice the (mover + blocker) radii, eight passes at most.
  const r = base / 2;
  const maxR = Math.max(0, ...obstacles.map(o => Number((o as { r?: number }).r ?? 0)), ...doubtful.map(d => d.r));
  const wander = 16 * (r + maxR);
  for (const d of doubtful) {
    const reach = wander + r + d.r;
    if (Math.hypot(d.x - end.x, d.y - end.y) <= reach || Math.hypot(d.x - start.x, d.y - start.y) <= reach) return { status: 'unknown', reason: d.reason };
  }
  const encounterRow = record(input.encounter.encounters) ?? input.encounter;
  const inside = Number(scalar(encounterRow.encounter_inside) ?? 0);
  const roofs = record(record(shape.authoring as unknown)?.buildingRoofs);
  const roofsUnknown = !read.runtimeShape && roofs !== null && Object.keys(roofs).length > 0;
  const overlayUnknown = !read.runtimeShape && !wallContact && ((Array.isArray(shape.openings) && shape.openings.length > 0) || roofsUnknown || (Array.isArray(shape.sceneWalls) && shape.sceneWalls.length > 0));
  return resolveMoveDestination({ shape, canvasExterior: inside !== 1, overlayUnknown, roofsUnknown, persisted: start, proposed: end, free,
    allowSqueeze: read.allowSqueeze ?? (!!encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') && !read.meshBody?.(row)),
    body: { diameter: base, height, baseFt: read.geometry?.(row)?.rulesDiameter ?? base, mesh: read.meshBody?.(row) ?? false,
      squeezeSizeFt: squeezeBodyFt(base, moverMovementInt(row, read, 'squeeze_size_category_offset')) }, obstacles });
}

/** enforce_creature_z_axis_floor() (FT3D): never below the floor under (x, y) on the band the row's PERSISTED z stands on. */
export function groundClamp(shape: RestShape, row: Row, anchorZ: number, p: Point, read: ForcedMoveReaders): Point {
  if (upper(record(row.encounter_creatures)?.preferred_speed_type) === 'ACFB413D-15A8-835B-C821-9E101B6C4F60') return p; // burrowing
  const band = floorBandShape(shape, p.x, p.y, anchorZ);
  const floor = band ? (floorAt(band.shape as never, p.x, p.y) ?? band.floor) : (floorAt(shape as never, p.x, p.y) ?? 0);
  void read;
  return p.z < Number(floor) - EPS ? { ...p, z: Number(floor) } : p;
}

/** Shared push/pull vector for creature and latched-inventory targets. No placement or mutation. */
export function forcedMovementVector(input: ForcedMoveInput, read: Pick<ForcedMoveReaders, 'position'>):
  { kind: 'move'; start: Point; end: Point; unit: Point } | Extract<ForcedMoveResult, { kind: 'none' | 'handback' }> {
  const { push, pull, toCasterCenter: toCaster, target, caster } = input;
  const [x, y, z] = read.position(target);
  if (![x, y, z].every(Number.isFinite)) return hand('position');
  const start = { x, y, z };
  // A placed cast supplies its current midpoint; unplaced casts use the held template or caster position.
  const aoe = input.aoe ?? null;
  const originX = (row: Row): boolean => present(record(row.position)?.status_origin_point_x);
  const hasAoe = !!aoe && (posCoord(aoe, 'x_pos') !== undefined || originX(aoe));
  const ref = (toCaster || !hasAoe) ? caster : aoe!;
  const tx = ref.target_x_pos;
  const refHasPoint = (Array.isArray(tx) && present(tx[0])) || originX(ref) || posCoord(ref, 'x_pos') !== undefined;
  const [rx, ry, rz] = idOf(ref) ? read.position(ref) : [NaN, NaN, NaN];
  const rp = !toCaster && input.reference !== undefined ? input.reference : idOf(ref) ? { x: rx, y: ry, z: rz }
    : { x: refHasPoint ? targetPoint(ref, 'x') : start.x, y: aoeCenterY(ref) ?? start.y, z: refHasPoint ? targetPoint(ref, 'z') : start.z };
  if (!rp || ![rp.x, rp.y, rp.z].every(Number.isFinite)) return hand('reference-position');
  const dx = start.x - rp.x, dy = start.y - rp.y, dv = start.z - rp.z;
  const dist = Math.sqrt(dx * dx + dy * dy + dv * dv);
  if (dist < 0.001) return { kind: 'none', why: 'at-reference' };
  const ux = dx / dist, uy = dy / dist, uv = dv / dist;
  let end: Point;
  if (toCaster || (push <= 0 && pull >= dist)) end = { ...rp };
  else if (push > 0) end = { x: start.x + ux * push, y: start.y + uy * push, z: start.z + uv * push };
  else end = { x: start.x - ux * pull, y: start.y - uy * pull, z: start.z - uv * pull };
  return { kind: 'move', start, end, unit: { x: ux, y: uy, z: uv } };
}

export function resolveForcedMovement(input: ForcedMoveInput, read: ForcedMoveReaders): ForcedMoveResult {
  if (read.runtimeShape) input = { ...input, shape: read.runtimeShape };
  const { push, pull, toCasterCenter: toCaster, target, caster, encounter, shape } = input;
  const floorRefusal = inventoryFloorRefusal(encounter);
  if (floorRefusal) return hand(floorRefusal);
  if (![push, pull].every(Number.isFinite)) return hand('distance-formula');
  if (push <= 0 && pull <= 0 && !toCaster) return { kind: 'none', why: 'no-distance' };
  if (!shape || !Array.isArray(shape.boxes) || shape.boxes.length === 0) return hand('legacy-axes');
  if (!idOf(target)) return hand('non-creature-target');
  const vector = forcedMovementVector(input, read);
  if (vector.kind !== 'move') return vector;
  const { start, unit: { x: ux, y: uy, z: uv } } = vector;
  let end = vector.end;
  if (LINKS.some(key => read.hasStatus(target, key))) return hand('transport-compound');
  const base = read.baseSizeFt(target), height = read.heightFt(target);
  if (base === null || height === null) return hand('body');
  // Structural contact truncates the sweep before any prop or creature beyond the wall can be struck.
  const stopped = wallStop(shape, start, end, base, height);
  end = stopped.end;
  const { wallContact } = stopped;
  const sweepFt = Math.hypot(end.x - start.x, end.y - start.y, end.z - start.z);
  // Geometry-only callers without inventory facts retain their existing conservative path/landing refusal.
  const inventoryNear = (from: Point, to: Point, radius: number): boolean | null => {
    for (const inv of rows(encounter.encounter_inventories_arr)) {
      if (Number(readInventoryField(inv, 'inventory_deleted') ?? 0) === 1 || !inventoryHasPosition(inv, read.position)) continue;
      const pass = read.inventoryPassThrough(inv);
      if (pass === null) return null;
      if (read.inventoryCollision) {
        if (pass || read.inventoryCollision(inv)) continue;
        return null;
      }
      // Its footprint's own reach from its centre (a transformed mesh can be far wider than its size tier — Codex S3 F3).
      const extent = read.inventoryExtentFt(inv);
      if (extent === null) return null;
      const [ix, iy] = read.position(inv);
      if (![ix, iy].every(Number.isFinite)) return null;
      const bound = extent + radius + 1;
      // A sweep pass-through row (a ladder, stairs, a no-cover prop) still matters at the LANDING — the destination resolver treats
      // passage props as no-stand obstacles (ft_ground_occupancy.php, Codex S3 F2) — so it is judged against the end point only.
      if (pass) { if (Math.hypot(ix - to.x, iy - to.y) <= bound) return true; continue; }
      const len = Math.hypot(to.x - from.x, to.y - from.y);
      const t = len < EPS ? 0 : Math.max(0, Math.min(1, ((ix - from.x) * (to.x - from.x) + (iy - from.y) * (to.y - from.y)) / (len * len)));
      if (Math.hypot(ix - (from.x + t * (to.x - from.x)), iy - (from.y + t * (to.y - from.y))) <= bound) return true;
    }
    return false;
  };
  const near0 = inventoryNear(start, end, base / 2);
  if (near0 === null) return hand('inventory-definition');
  if (near0) return hand('inventory');

  // ── the collision sweep (push only): creatures are a hard stop ──
  let collision: CollisionPlan | null = null;
  let inventoryMove: ForcedMovePlan['inventoryMove'];
  let inventoryDragged: ForcedMovePlan['dragged'] = [];
  if (!toCaster && push > 0) {
    const horizontal = Math.hypot(ux, uy);
    if (horizontal > EPS) {
      const sux = ux / horizontal, suy = uy / horizontal, length = sweepFt * horizontal;
      const hw = base / 2, hh = base / 2;
      const overlapAt = (blocker: Row, rayFt: number, bh = read.heightFt(blocker)): boolean | null => {
        if (bh === null) return null;
        const mb = start.z + uv * rayFt, mt = mb + Math.max(0.01, height);
        const bb = read.position(blocker)[2], bt = bb + Math.max(0.01, bh);
        return mt > bb + 0.01 && bt > mb + 0.01;
      };
      let best: { D: number; id: string; kind: CollisionPlan['kind']; capacity: number; row: Row } | null = null;
      // Inventory wins equal-distance ties, as in the legacy inventory-first sweep.
      if (read.inventoryCollision) for (const inv of rows(encounter.encounter_inventories_arr)) {
        if (inventoryBlocksSweep(inv, read, target) === false) continue;
        const body = read.inventoryCollision(inv, target);
        if (!body) return hand('inventory-body');
        const D = sweptObstacleEntryDistance(body.obstacle, start.x, start.y, sux, suy, base / 2, length);
        if (D === null) continue;
        const bottom = start.z + uv * D / horizontal;
        if (bottom + height <= body.obstacle.zMin + .01 || body.obstacle.zMax <= bottom + .01) continue;
        if (!best || D < best.D) best = { D, id: upper(readInventoryField(inv, 'inventory_id')), kind: 'inventory', capacity: body.capacity, row: inv };
      }
      for (const other of rows(encounter.encounter_creatures_arr)) {
        const id = idOf(other);
        if (id === '' || id === idOf(target)) continue;
        const ob = read.baseSizeFt(other);
        if (ob === null) return hand('other-body');
        const [ox, oy, oz] = read.position(other);
        if (![ox, oy, oz].every(Number.isFinite)) return hand('other-position');
        const D = sweptEntryDistance({ left: ox - ob / 2, right: ox + ob / 2, near: oy - ob / 2, far: oy + ob / 2 }, start.x, start.y, sux, suy, hw, hh, length);
        if (D === null) continue;
        const over = overlapAt(other, D / horizontal);
        if (over === null) return hand('other-body');
        if (!over) continue;
        if (!best || D < best.D) best = { D, id, kind: 'creature', capacity: Infinity, row: other };
      }
      if (best) {
        const D = best.D / horizontal;
        const remaining = push - D;
        const absorbed = Math.round(Math.min(remaining, best.capacity));
        const shove = Math.max(0, remaining - best.capacity);
        if (shove > EPS) {
          const body = read.inventoryCollision!(best.row)!;
          if (body.shoveRefusal) return hand(body.shoveRefusal);

          const [ix, iy, iz] = read.position(best.row), from = { x: ix, y: iy, z: iz };
          const proposed = { x: ix + ux * shove, y: iy + uy * shove, z: iz + uv * shove };
          const obstacle = body.obstacle;
          const radius = obstacle.kind === 'circle' ? obstacle.r : Math.hypot(obstacle.halfW, obstacle.halfD);
          const stopped = wallStop(shape, from, proposed, radius * 2, body.height);
          const placed = destination(input, read, best.row, from, stopped.end, Math.abs(uv * shove) > EPS, stopped.wallContact);
          if (placed.status === 'unknown') return hand('inventory-shove-destination:' + placed.reason);
          if (placed.status === 'placed') {
            inventoryMove = { id: best.id, start: from, end: { x: placed.x, y: placed.y, z: placed.z } };
            if (compoundActorIds(encounter, best.row, read).size > 1) {
              const team = planLatchedObjectMove(encounter, shape, best.row, inventoryMove.end, {
                ...read, propFootprint: row => {
                  const body = read.inventoryCollision?.(row);
                  return body ? { ...body.obstacle, heightFt: body.height } : null;
                },
              });
              if (team.kind !== 'move') return hand(team.kind === 'handback' ? team.reason : 'inventory-shove-compound');
              inventoryDragged = team.members.map(member => ({ id: member.id, start: member.before, end: member.after, fall: { kind: 'none', why: 'floor' } }));
            }
          }
        }
        const travel = Math.min(sweepFt, Math.max(0, push - absorbed));
        const dice = absorbed >= 5 ? Math.max(1, Math.floor(absorbed / 10)) : 0;
        collision = { kind: best.kind, id: best.id, contactFt: D, absorbedFt: absorbed, dice, struckDice: dice };
        end = { x: start.x + ux * travel, y: start.y + uy * travel, z: start.z + uv * travel };
      }
    }
  }
  // ── the ground clamp, then the destination resolver (walk snap unless the push changes height) ──
  end = groundClamp(shape, target, start.z, end, read);
  // Retain off-floor feet until the fall lane resolves support at the landing.
  const landingFloor = fallingShapeSupportFloor(shape, end.x, end.y, start.z, read.geometry(target)?.rulesDiameter ?? base);
  const free = Math.abs(end.z - start.z) > EPS || (read.inventoryCollision !== undefined
    && (landingFloor === null || start.z > landingFloor + EPS));
  const drag = read.hasStatus(target, 'grappling');
  const grappledIds = new Set<string>();
  if (drag) {
    for (const other of rows(encounter.encounter_creatures_arr)) {
      if (idOf(other) === idOf(target)) continue;
      if (read.statuses(other).some(s => upper(s.status_caster_id) === idOf(target) && upper(statusItemIdOf(s)) === GRAPPLED_ITEM_ID))
        grappledIds.add(idOf(other));
    }
  }
  // ft_resolve_move_destination() (session 3 step 2): placed — maybe NUDGED — or BLOCKED (move_target() returns: the target stays; the drag
  // follows the applied delta, zero) — or a shape / body the page cannot judge (hand back).
  // The object is shoved before the creature lands. Judge against its planned position on a private geometry view.
  const landingInput = inventoryMove ? { ...input, encounter: { ...encounter,
    encounter_inventories_arr: rows(encounter.encounter_inventories_arr).map(inv => upper(readInventoryField(inv, 'inventory_id')) === inventoryMove!.id
      ? { ...inv, position: { x_pos: inventoryMove!.end.x, y_pos: inventoryMove!.end.y, z_pos: inventoryMove!.end.z } } : inv) } } : input;
  const landed = destination(landingInput, read, target, start, end, free, wallContact);
  if (landed.status === 'unknown') return hand('destination-resolver:' + landed.reason);
  if (landed.status === 'blocked') {
    // Codex step-2 review F1: PHP leaves the row where it is but still runs the falls (the target, and every grapplee at a zero delta) and
    // the level-triggered zone reconcile — `none` only when none of them can do anything.
    const stay = predictFall({ row: target, encounter, shape }, read);
    if (stay.kind !== 'none' || stay.snapZ !== undefined) return hand('destination-blocked-fall');
    for (const other of rows(encounter.encounter_creatures_arr)) {
      if (!grappledIds.has(idOf(other))) continue;
      const f = predictFall({ row: other, encounter, shape, grappler: target }, read);
      if (f.kind !== 'none' || f.snapZ !== undefined) return hand('destination-blocked-fall');
    }
    const zones = [encounter, ...rows(encounter.encounter_creatures_arr), ...rows(encounter.encounter_inventories_arr)]
      .some(row => read.statuses(row).some(s => Number(scalar(s.status_aoe_source) ?? 0) === 1));
    if (zones) return hand('destination-blocked-zone');
    // A collision that rolls dice (≥ 5 ft absorbed) still deals its damage — resolve_push_collision_with_inventory() runs BEFORE
    // move_target(), which then refuses the landing (the target stays): a stationary plan carrying the collision for the damage writer
    // (Session A2 R-108 bucket 2 — was a hand-back, `destination-blocked-collision`).
    if ((collision && collision.dice > 0) || inventoryMove) return { kind: 'move', start, end: { ...start }, collision,
      ...(inventoryMove ? { inventoryMove } : {}), fall: stay, dragged: inventoryDragged, zoneReconcile: false };
    return { kind: 'none', why: 'destination-blocked' };
  }
  end = groundClamp(shape, target, start.z, { x: landed.x, y: landed.y, z: landed.z }, read);
  // a nudge may carry the landing toward inventory the path check did not see
  if (landed.moved) { const nearNudge = inventoryNear(end, end, base / 2); if (nearNudge !== false) return hand('inventory'); }
  if (Math.hypot(end.x - start.x, end.y - start.y, end.z - start.z) < 0.001 && !collision?.dice && !inventoryMove) return { kind: 'none', why: 'no-displacement' };
  const delta = { x: end.x - start.x, y: end.y - start.y, z: end.z - start.z };
  // ── the grapple drag-along: every row this target grapples follows the APPLIED delta (immune rows stay) ──
  const dragged: ForcedMovePlan['dragged'] = [...inventoryDragged];
  for (const other of rows(encounter.encounter_creatures_arr)) {
    const id = idOf(other);
    if (!grappledIds.has(id)) continue;
    // PHP's dragged fall resolves the row's grappler from its Grappled rows: more than one → which is chosen is not modelled (F9).
    if (read.statuses(other).filter(st => upper(statusItemIdOf(st)) === GRAPPLED_ITEM_ID).length !== 1) return hand('drag-multiple-grapplers');
    const immune = read.immuneToForcedMovement(other);
    if (immune === null) return hand('drag-defense');
    if (immune) continue;
    const [ox, oy, oz] = read.position(other);
    if (![ox, oy, oz].every(Number.isFinite)) return hand('drag-position');
    const from = { x: ox, y: oy, z: oz };
    let to = groundClamp(shape, other, oz, { x: ox + delta.x, y: oy + delta.y, z: oz + delta.z }, read);
    const dragBase = read.baseSizeFt(other), dragHeight = read.heightFt(other);
    if (dragBase === null || dragHeight === null || !(dragBase > 0) || !(dragHeight > 0)) return hand('drag-destination:body');
    // Owner ruling: a dragged body stops at the wall it meets; the grappler keeps its applied delta.
    const stopped = wallStop(shape, from, to, dragBase, dragHeight);
    to = stopped.end;
    const nearDrag = inventoryNear(from, to, dragBase / 2);
    if (nearDrag !== false) return hand('drag-inventory');
    const placed = destination(landingInput, read, other, from, to, Math.abs(delta.z) > EPS, stopped.wallContact); // the grappler is in its compound (never an obstacle)
    if (placed.status === 'unknown') return hand('drag-destination:' + placed.reason);
    // a refused drag destination leaves that row where it was (move_target returns) — it still takes its fall check (Codex F1)
    if (placed.status === 'blocked') to = from;
    else to = groundClamp(shape, other, oz, { x: placed.x, y: placed.y, z: placed.z }, read);
    if (placed.status === 'placed' && placed.moved && inventoryNear(to, to, dragBase / 2) !== false) return hand('drag-inventory');
    dragged.push({ id, start: from, end: to, fall: { kind: 'none', why: 'floor' } });
  }
  // ── falls: the target, then each dragged row (the drag's own hold is gone once the target lands) ──
  const fall = predictFall({ row: target, at: end, encounter: landingInput.encounter, shape }, read);
  if (fall.kind === 'handback') return { kind: 'handback', reason: 'forced-move:' + fall.reason };
  for (const d of dragged) {
    const row = rows(encounter.encounter_creatures_arr).find(r => idOf(r) === d.id)!;
    const f = predictFall({ row, at: d.end, encounter: landingInput.encounter, shape, grappler: target }, read);
    // A grappled row's own fall asks for its grappler (PHP: the airborne-grappler lookup) — the twin hands those back.
    if (f.kind === 'handback') return { kind: 'handback', reason: 'forced-move:dragged-' + f.reason };
    d.fall = f;
  }
  const zoneReconcile = [encounter, ...rows(encounter.encounter_creatures_arr), ...rows(encounter.encounter_inventories_arr)]
    .some(row => read.statuses(row).some(s => Number(scalar(s.status_aoe_source) ?? 0) === 1));
  return { kind: 'move', start, end, collision, ...(inventoryMove ? { inventoryMove } : {}), fall, dragged, zoneReconcile };
}
